import React, { useState, useEffect } from "react";
import { ReviewFormProps, ReviewFormData } from "../../types/ReviewTypes";
import { authFetch } from "../../utils/authUtils";
import "./Review.css";

const ReviewForm: React.FC<ReviewFormProps> = ({
    songId,
    itemId,
    itemType = 'song',
    isOpen,
    onClose,
    existingReview,
    onReviewAdded,
    onReviewUpdated
}) => {
    // Support both legacy songId and new itemId approach
    const actualItemId = itemId || songId;
    const actualItemType = itemType;

    const [formData, setFormData] = useState<ReviewFormData>({
        rating: existingReview ? Number(existingReview.rating) : 3,
        content: existingReview?.content || ""
    });
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (existingReview) {
            setFormData({
                rating: Number(existingReview.rating),
                content: existingReview.content
            });
        } else {
            setFormData({
                rating: 3,
                content: ""
            });
        }
    }, [existingReview, isOpen]);

    const handleRatingChange = (newRating: number) => {
        setFormData(prev => ({ ...prev, rating: newRating }));
    };

    const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFormData(prev => ({ ...prev, content: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.content.trim().length === 0) {
            setError("Review content cannot be empty");
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            let response;

            if (existingReview) {
                // Update existing review
                response = await authFetch(`/api/reviews/${existingReview.id}`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(formData)
                });
            } else {
                // Create new review - handle both song and album reviews
                const apiEndpoint = actualItemType === 'album'
                    ? "/api/reviews/album"
                    : "/api/reviews";

                response = await authFetch(apiEndpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        [`${actualItemType}_id`]: actualItemId,
                        ...formData
                    })
                });
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to submit review");
            }

            const reviewData = await response.json();

            if (existingReview) {
                onReviewUpdated(reviewData);
            } else {
                onReviewAdded(reviewData);
            }

            // Dispatch custom event to notify that a review was added or updated
            window.dispatchEvent(new CustomEvent('reviewUpdated'));

            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="review-modal-backdrop">
            <div className="review-modal">
                <div className="review-modal-header">
                    <h3>{existingReview ? "edit" : "add review"}</h3>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="rating-input">
                        <label><p className="rating-label">RATING</p></label>
                        <div className="star-rating">
                            {[1, 2, 3, 4, 5].map(starIndex => (
                                <div
                                    key={starIndex}
                                    className="star-container"
                                >
                                    <div
                                        className="star-half left"
                                        onClick={() => handleRatingChange(starIndex === 1 ? 0.5 : starIndex - 0.5)}
                                    ></div>
                                    <div
                                        className="star-half right"
                                        onClick={() => handleRatingChange(starIndex)}
                                    ></div>
                                    <i
                                        className={`fa ${formData.rating >= starIndex ? 'fa-star' :
                                            formData.rating === starIndex - 0.5 ? 'fa-star-half-o' :
                                                'fa-star-o'
                                            }`}
                                        aria-hidden="true"
                                    ></i>
                                </div>
                            ))}
                            <span className="rating-value">{formData.rating.toFixed(1)}</span>
                        </div>
                    </div>

                    <div className="review-content">
                        <label htmlFor="review-content"><p className="rating-label">REVIEW</p></label>
                        <textarea
                            id="review-content"
                            value={formData.content}
                            onChange={handleContentChange}
                            placeholder="Write your review here..."
                            rows={5}
                            required
                        />
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <div className="form-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            <p className="review-button-text">cancel</p>
                        </button>
                        <button
                            type="submit"
                            className="submit-button"
                            disabled={isSubmitting || formData.content.trim().length === 0}
                        >
                            <p className="review-button-text"> {isSubmitting ? "submitting..." : existingReview ? "update" : "submit"}</p>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReviewForm; 